import { Switch, Route } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/lib/auth-context";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { AdminRoute } from "@/components/AdminRoute";
import { AdminLayout } from "@/components/AdminLayout";
import { Toaster } from "@/components/ui/toaster";

// ─── Pages ────────────────────────────────────────────────────────────────────
import AdminLogin          from "@/pages/AdminLogin";
import Dashboard           from "@/pages/Dashboard";
import Users               from "@/pages/Users";
import AdminAIManagement   from "@/pages/AdminAIManagement";
import AdminIntegrations   from "@/pages/AdminIntegrations";
import AdminLandingPage    from "@/pages/AdminLandingPage";
import AdminSubscriptions  from "@/pages/AdminSubscriptions";
import AdminVouchers       from "@/pages/AdminVouchers";
import HelpdeskAdmin       from "@/pages/HelpdeskAdmin";
import AppSettings         from "@/pages/AppSettings";
import DensityReferences   from "@/pages/DensityReferences";
import YieldReferences     from "@/pages/YieldReferences";
import Announcements       from "@/pages/Announcements";
import EmailManagement     from "@/pages/EmailManagement";
import EmailCampaigns      from "@/pages/EmailCampaigns";
import EmailSequences      from "@/pages/EmailSequences";
import CampaignManager     from "@/pages/CampaignManager";
// ─── New pages (ported from mockms) ──────────────────────────────────────────
import ReportsTransactions         from "@/pages/ReportsTransactions";
import ReportsExport               from "@/pages/ReportsExport";
import AIUsageLogs                 from "@/pages/AIUsageLogs";
import AIChatFeedback              from "@/pages/AIChatFeedback";
import Inbox                       from "@/pages/Inbox";
import UserGuideMediaManagement    from "@/pages/UserGuideMediaManagement";
import PaymentSettings             from "@/pages/PaymentSettings";
import SubscriptionPlans           from "@/pages/SubscriptionPlans";
import HelpdeskNotificationEmails  from "@/pages/HelpdeskNotificationEmails";
import HelpdeskTicket              from "@/pages/HelpdeskTicket";
import EmailTemplates              from "@/pages/EmailTemplates";
import ActivityLogs                from "@/pages/ActivityLogs";
import { ServerHistory }            from "@/components/admin/stats/ServerHistory";
import WorkspaceSettings           from "@/pages/WorkspaceSettings";
import TeamSettings                from "@/pages/TeamSettings";
import WorkspaceRoles              from "@/pages/WorkspaceRoles";
import ChefAiqLibrary              from "@/pages/ChefAiqLibrary";
import StarterPack                 from "@/pages/StarterPack";
import AddStarterIngredient        from "@/pages/AddStarterIngredient";
import AddStarterMaterial          from "@/pages/AddStarterMaterial";
import AddStarterEquipment         from "@/pages/AddStarterEquipment";
import ViewStarterIngredient       from "@/pages/ViewStarterIngredient";
import ViewStarterMaterial         from "@/pages/ViewStarterMaterial";
import ViewStarterEquipment        from "@/pages/ViewStarterEquipment";
import Impersonate                 from "@/pages/Impersonate";
import ChefAiqRecipes              from "@/pages/ChefAiqRecipes";

// ──────────────────────────────────────────────────────────────────────────────

/** Wraps a page in both the auth guard and the admin layout shell */
function AdminPage({ children }: { children: React.ReactNode }) {
  return (
    <AdminRoute>
      <AdminLayout>{children}</AdminLayout>
    </AdminRoute>
  );
}

function Router() {
  return (
    <Switch>
      {/* ── Public ── */}
      <Route path="/signin" component={AdminLogin} />

      {/* ── Core admin ── */}
      <Route path="/"><AdminPage><Dashboard /></AdminPage></Route>
      <Route path="/users"><AdminPage><Users /></AdminPage></Route>

      {/* ── Subscriptions & payments ── */}
      <Route path="/subscriptions"><AdminPage><AdminSubscriptions /></AdminPage></Route>
      <Route path="/subscription-plans"><AdminPage><SubscriptionPlans /></AdminPage></Route>
      <Route path="/payment-settings"><AdminPage><PaymentSettings /></AdminPage></Route>
      <Route path="/vouchers"><AdminPage><AdminVouchers /></AdminPage></Route>

      {/* ── Reports ── */}
      <Route path="/reports/sales"><AdminPage><ReportsTransactions /></AdminPage></Route>
      <Route path="/reports/activity-logs"><AdminPage><ActivityLogs /></AdminPage></Route>
      <Route path="/export/users"><AdminPage><ReportsExport /></AdminPage></Route>
      <Route path="/server-history"><AdminPage><ServerHistory /></AdminPage></Route>
      <Route path="/ai-usage"><AdminPage><AIUsageLogs /></AdminPage></Route>
      <Route path="/ai-chat-feedback"><AdminPage><AIChatFeedback /></AdminPage></Route>

      {/* ── Messaging ── */}
      <Route path="/announcements"><AdminPage><Announcements /></AdminPage></Route>
      <Route path="/email-management"><AdminPage><EmailManagement /></AdminPage></Route>
      <Route path="/email-campaigns"><AdminPage><EmailCampaigns /></AdminPage></Route>
      <Route path="/email-sequences"><AdminPage><EmailSequences /></AdminPage></Route>
      <Route path="/email-templates"><AdminPage><EmailTemplates /></AdminPage></Route>
      <Route path="/inbox"><AdminPage><Inbox /></AdminPage></Route>
      <Route path="/messaging/notification-emails"><AdminPage><HelpdeskNotificationEmails /></AdminPage></Route>

      {/* ── Campaigns ── */}
      <Route path="/campaigns"><AdminPage><CampaignManager /></AdminPage></Route>

      {/* ── AI & integrations ── */}
      <Route path="/ai-management"><AdminPage><AdminAIManagement /></AdminPage></Route>
      <Route path="/integrations"><AdminPage><AdminIntegrations /></AdminPage></Route>

      {/* ── Support ── */}
      <Route path="/helpdesk"><AdminPage><HelpdeskAdmin /></AdminPage></Route>
      <Route path="/helpdesk-ticket/:ticketId"><AdminPage><HelpdeskTicket /></AdminPage></Route>

      {/* ── Content ── */}
      <Route path="/landing-page"><AdminPage><AdminLandingPage /></AdminPage></Route>
      <Route path="/user-guide-media"><AdminPage><UserGuideMediaManagement /></AdminPage></Route>
      <Route path="/chefaiq-library"><AdminPage><ChefAiqLibrary /></AdminPage></Route>
      <Route path="/chefaiq-library/recipes"><AdminPage><ChefAiqRecipes /></AdminPage></Route>
      <Route path="/starter-pack"><AdminPage><StarterPack /></AdminPage></Route>
      <Route path="/starter-pack/ingredient"><AdminPage><AddStarterIngredient /></AdminPage></Route>
      <Route path="/starter-pack/ingredient/view"><AdminPage><ViewStarterIngredient /></AdminPage></Route>
      <Route path="/starter-pack/material"><AdminPage><AddStarterMaterial /></AdminPage></Route>
      <Route path="/starter-pack/material/view"><AdminPage><ViewStarterMaterial /></AdminPage></Route>
      <Route path="/starter-pack/equipment"><AdminPage><AddStarterEquipment /></AdminPage></Route>
      <Route path="/starter-pack/equipment/view"><AdminPage><ViewStarterEquipment /></AdminPage></Route>

      {/* ── Workspace ── */}
      <Route path="/workspace-settings"><AdminPage><WorkspaceSettings /></AdminPage></Route>
      <Route path="/team-settings"><AdminPage><TeamSettings /></AdminPage></Route>
      <Route path="/workspace-roles"><AdminPage><WorkspaceRoles /></AdminPage></Route>

      {/* ── User tools ── */}
      <Route path="/impersonate"><AdminPage><Impersonate /></AdminPage></Route>

      {/* ── System ── */}
      <Route path="/settings"><AdminPage><AppSettings /></AdminPage></Route>
      <Route path="/density-references"><AdminPage><DensityReferences /></AdminPage></Route>
      <Route path="/yield-references"><AdminPage><YieldReferences /></AdminPage></Route>

      {/* ── 404 fallback ── */}
      <Route><AdminPage><Dashboard /></AdminPage></Route>
    </Switch>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WorkspaceProvider>
          <Router />
          <Toaster />
        </WorkspaceProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
